import React, { useRef } from 'react'
import { Trash2 } from "lucide-react";
import { useDispatch } from 'react-redux';
import { 
    removeFromCart, 
    updateCartItemQuantity,
    optimisticUpdateQuantity,
    optimisticRemove,
    rollbackCart
} from "../../../redux/slices/cartSlice";

const CartContents = ({cart, userId, guestId}) => {
    const dispatch = useDispatch();
    
    // Track pending API calls to prevent overlapping requests
    const pendingUpdates = useRef(new Map());

    // Handle quantity changes with debounced API calls
    const handleAddToCart = async (productId, delta, quantity, size, color) => {
        const currentQuantity = Number(quantity) || 0;
        const newQuantity = currentQuantity + delta;
        
        if (newQuantity >= 1) {
            // Create unique key for this product variant
            const itemKey = `${productId}-${size}-${color}`;
            
            // 1. Update UI immediately (optimistic)
            dispatch(optimisticUpdateQuantity({
                productId,
                quantity: newQuantity,
                size,
                color
            }));
            
            // 2. Cancel any pending API call for this item
            if (pendingUpdates.current.has(itemKey)) {
                clearTimeout(pendingUpdates.current.get(itemKey));
            }
            
            // 3. Debounce API call (wait 300ms after last click)
            const timeoutId = setTimeout(async () => {
                try {
                    await dispatch(updateCartItemQuantity({
                        productId,
                        quantity: newQuantity,
                        guestId,
                        userId,
                        size,
                        color,
                    })).unwrap();
                } catch (error) {
                    console.error('Failed to update cart:', error);
                    // On error, fetch fresh cart data from server
                    // This ensures we're always in sync
                } finally {
                    pendingUpdates.current.delete(itemKey);
                }
            }, 300);
            
            pendingUpdates.current.set(itemKey, timeoutId);
        }
    };

    const handleRemoveFromCart = async (productId, size, color) => {
        const itemKey = `${productId}-${size}-${color}`;
        
        // Cancel any pending updates for this item
        if (pendingUpdates.current.has(itemKey)) {
            clearTimeout(pendingUpdates.current.get(itemKey));
            pendingUpdates.current.delete(itemKey);
        }
        
        // Save current cart state for rollback
        const previousCart = JSON.parse(JSON.stringify(cart));
        
        // 1. Update UI immediately (optimistic)
        dispatch(optimisticRemove({
            productId,
            size,
            color
        }));
        
        // 2. Sync with backend
        try {
            await dispatch(removeFromCart({
                productId, 
                guestId, 
                userId, 
                size, 
                color
            })).unwrap();
        } catch (error) {
            console.error('Failed to remove from cart:', error);
            dispatch(rollbackCart(previousCart));
        }
    }

    // Safe total price calculation
    const totalPrice = (cart?.products && Array.isArray(cart.products) && cart.products.length > 0)
        ? cart.products.reduce((total, product) => {
            const price = Number(product?.price) || 0;
            const quantity = Number(product?.quantity) || 0;
            return total + (price * quantity);
        }, 0)
        : 0;

    // Safe total items calculation
    const totalItems = (cart?.products && Array.isArray(cart.products) && cart.products.length > 0)
        ? cart.products.reduce((total, product) => {
            const quantity = Number(product?.quantity) || 0;
            return total + quantity;
        }, 0)
        : 0;

    return (
        <>
            <div>
                {(cart?.products && Array.isArray(cart.products) && cart.products.length > 0) ? (
                    cart.products.map((product) => {
                        const price = Number(product?.price) || 0;
                        const quantity = Number(product?.quantity) || 0;
                        const totalItemPrice = price * quantity;

                        return (
                            <div key={`${product?.productId}-${product?.size}-${product?.color}`} className="flex items-start justify-between py-4 border-b">
                                <div className="flex item-start">
                                    <img 
                                        src={product?.image || ''} 
                                        alt={product?.name || 'Product'} 
                                        className="w-20 h-24 object-cover mr-4 rounded" 
                                    />
                                
                                    <div>
                                        <h3>{product?.name || 'Unknown Product'}</h3>
                                        <p className="text-sm text-gray-500">
                                            Size: {product?.size || 'N/A'} | Color: {product?.color || 'N/A'}
                                        </p>
                                        <div className="flex items-center mt-2">
                                            <button 
                                                onClick={() => handleAddToCart(
                                                    product?.productId, 
                                                    -1, 
                                                    quantity, 
                                                    product?.size, 
                                                    product?.color
                                                )}
                                                className="border rounded px-2 py-1 text-xl font-medium hover:bg-gray-100 active:bg-gray-200 transition-colors select-none"
                                            >
                                                -
                                            </button>
                                            <span className="mx-2 text-lg min-w-[30px] text-center">{quantity}</span>
                                            <button 
                                                onClick={() => handleAddToCart(
                                                    product?.productId, 
                                                    1, 
                                                    quantity, 
                                                    product?.size, 
                                                    product?.color
                                                )} 
                                                className="border rounded px-2 py-1 text-xl font-medium hover:bg-gray-100 active:bg-gray-200 transition-colors select-none"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="font-medium">₹{totalItemPrice.toLocaleString()}</p>
                                    <p className="text-sm text-gray-500">₹{price.toLocaleString()} each</p>
                                    <button 
                                        onClick={() => handleRemoveFromCart(
                                            product?.productId, 
                                            product?.size, 
                                            product?.color
                                        )}
                                        className="hover:text-red-600 transition-colors"
                                    >
                                        <Trash2 className="h-6 w-6 mt-2"/>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        Your cart is empty
                    </div>
                )}
            </div>

            {/* Total Price Section */}
            {(cart?.products && Array.isArray(cart.products) && cart.products.length > 0) && (
                <div className="fixed bottom-[115px] left-0 right-0 bg-white border-t-2 border-gray-200 p-4 z-10">
                    <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Total:</span>
                        <span className="text-xl font-bold text-green-600">
                            ₹{totalPrice.toLocaleString()}
                        </span>
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                        Total items: {totalItems}
                    </div>
                </div>
            )}
        </>
    );
}

export default CartContents;
